import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { createCount, listCounts } from '../../api/inventory'
import ErrorBanner from '../../components/ErrorBanner'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { fmtDay } from '../../utils/format'
import { money, num } from '../sales/salesShared'
import InventoryTabs from './InventoryTabs'
import { COUNT_STATUS, countTitle, qty, useLocations } from './inventoryShared'

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'counting', label: 'Counting' },
  { id: 'waiting', label: 'Waiting for approval' },
  { id: 'posted', label: 'Stock updated' },
  { id: 'cancelled', label: 'Cancelled' },
]

// Stock counts: people count the shelves with the phone app (or a barcode
// scanner here), then the stock is updated to match what was counted.
export default function StockCounts() {
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [starting, setStarting] = useState(false)
  const filter = FILTERS.some((f) => f.id === params.get('show')) ? params.get('show') : 'all'
  const setFilter = (id) => setParams(id === 'all' ? {} : { show: id }, { replace: true })

  useEffect(() => {
    listCounts()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.message || 'Could not load counts.'))
      .finally(() => setLoading(false))
  }, [])

  const counts = useMemo(() => Object.fromEntries(
    FILTERS.map((f) => [f.id, rows.filter((r) => f.id === 'all' || r.status === f.id).length])
  ), [rows])
  const shown = rows.filter((r) => filter === 'all' || r.status === filter)

  return (
    <div className="ci-page">
      <InventoryTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Counts</h1>
            <div className="ci-title-meta">Count the shelves with the phone app or a barcode scanner; the stock is updated to match.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-primary" onClick={() => setStarting(true)}><Plus size={15} />Start a count</button>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <FilterChips options={FILTERS.map((f) => ({ ...f, count: counts[f.id] }))} value={filter} onChange={setFilter} />

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th>Count</th>
                <th className="hide-sm">Where</th>
                <th className="num">Items</th>
                <th className="num hide-sm">Counted</th>
                <th className="num">Short</th>
                <th className="num">Extra</th>
                <th className="center">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7"><Spinner label="Loading counts…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="7"><div className="ci-empty">{rows.length ? 'No counts match.' : 'No counts yet. Start one here, or from the Inventory tab of the phone app.'}</div></td></tr>
              ) : shown.map((r) => {
                const st = COUNT_STATUS[r.status] || COUNT_STATUS.counting
                const done = r.status === 'waiting' || r.status === 'posted'
                return (
                  <tr key={r.count_id} className="row-link" onClick={() => nav(`/inventory/counts/${r.count_id}`)}>
                    <td>
                      <Link className="ci-je-link" to={`/inventory/counts/${r.count_id}`} onClick={(e) => e.stopPropagation()}>{countTitle(r)}</Link>
                      <span className="cell-sub">{[r.label, `${fmtDay(r.created_at) || ''}${r.created_by ? ` · ${r.created_by}` : ''}`].filter(Boolean).join(' · ')}</span>
                    </td>
                    <td className="hide-sm">{r.location_name}</td>
                    <td className="num">{qty(r.items)}</td>
                    <td className="num hide-sm">{qty(r.units)}</td>
                    <td className="num">{done && num(r.short_value) > 0 ? <span className="count-short">{money(r.short_value)}</span> : '–'}</td>
                    <td className="num">{done && num(r.over_value) > 0 ? <span className="count-extra">{money(r.over_value)}</span> : '–'}</td>
                    <td className="center"><StatusBadge label={st.label} tone={st.tone} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <StartCount open={starting} onClose={() => setStarting(false)} onStarted={(c) => nav(`/inventory/counts/${c.count_id}`)} />
    </div>
  )
}

function StartCount({ open, onClose, onStarted }) {
  const locations = useLocations()
  const [locationId, setLocationId] = useState('')
  const [label, setLabel] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { if (locations?.length && !locationId) setLocationId(String(locations[0].location_id)) }, [locations, locationId])
  useEffect(() => { if (open) { setLabel(''); setError('') } }, [open])

  const start = async () => {
    setSaving(true)
    setError('')
    try {
      onStarted(await createCount({ location_id: locationId ? Number(locationId) : undefined, label: label.trim() || undefined }))
    } catch (e) {
      setError(e.message || 'Could not start the count.')
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Start a count"
      subtitle="Others can join it from the phone app and scan at the same time."
      size="sm"
      footer={(
        <>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={start}>{saving ? 'Starting…' : 'Start counting'}</button>
        </>
      )}
    >
      {error && <ErrorBanner message={error} />}
      {locations?.length > 1 && (
        <label className="ci-form-field">
          <span>Where</span>
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
          </select>
        </label>
      )}
      <label className="ci-form-field">
        <span>What are you counting? (optional)</span>
        <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} placeholder="e.g. Aisle 3, Drinks fridge, Whole shop" />
      </label>
    </Modal>
  )
}
